/**
 * Edit form for one tutorial video.
 */
import * as React from 'react';
import { Clapperboard } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileDropzone } from '@/components/common/FileDropzone';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Input } from '@/components/forms/Input';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Select } from '@/components/forms/Select';
import { toast } from '@/components/ui/toast';
import { getRoles } from '@/services/roles.service';
import { getTutorialVideo, updateTutorialVideo } from '@/services/tutorials.service';
import type { TutorialVideoFormState, TutorialVideoMode } from '@/types/tutorial.types';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  TUTORIAL_AUDIENCE_ROLE_GROUPS,
  TUTORIAL_MODE_OPTIONS,
  TUTORIAL_VIDEO_ACCEPTED_FORMATS,
  TUTORIAL_VIDEO_MAX_SIZE_BYTES,
  tutorialKeys,
} from './tutorial-configs';
import {
  applyTutorialVideoFile,
  applyTutorialVideoMode,
  applyTutorialVideoName,
  applyTutorialVideoRoleKeys,
  buildTutorialRoleChoices,
  emptyTutorialVideoForm,
  isTutorialVideoEditFormValid,
  selectedChoiceKeys,
  tutorialRoleSelectOptions,
  tutorialStoredVideoPreview,
  tutorialVideoToForm,
} from './tutorial-form-utils';

/** Props for {@link EditTutorialFormView}. */
interface EditTutorialFormViewProps {
  videoId: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Slide-panel edit form. Loads `GET /tutorials/:id` before showing fields. */
export const EditTutorialFormView: React.FC<EditTutorialFormViewProps> = ({
  videoId,
  onClose,
  onSuccess,
}) => {
  const [draft, setDraft] = React.useState<TutorialVideoFormState | null>(null);

  const detailQuery = useQuery({
    queryKey: tutorialKeys.detail(videoId),
    queryFn: () => getTutorialVideo(videoId),
  });

  const rolesQuery = useQuery({
    queryKey: tutorialKeys.roles,
    queryFn: () => getRoles({ roleGroups: [...TUTORIAL_AUDIENCE_ROLE_GROUPS] }),
  });

  const form =
    draft ?? (detailQuery.data ? tutorialVideoToForm(detailQuery.data) : emptyTutorialVideoForm);
  const roles = rolesQuery.data?.data ?? [];
  const roleChoices = buildTutorialRoleChoices(roles, form.mode);
  const selectedRoles = selectedChoiceKeys(roleChoices, form.roleIds);
  const roleOptions = tutorialRoleSelectOptions(roleChoices);
  const storedPreview = tutorialStoredVideoPreview(form, detailQuery.data);

  const saveMutation = useMutation({
    mutationFn: () => updateTutorialVideo(videoId, form),
    onSuccess: () => {
      toast.success('Vidéo modifiée');
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isValid = isTutorialVideoEditFormValid(form) && !!detailQuery.data;

  /** PATCH the tutorial when the edit form is valid. */
  function handleSubmit(): void {
    if (!isValid) return;
    saveMutation.mutate();
  }

  function handleFileSelect(file: File | null): void {
    setDraft((current) => applyTutorialVideoFile(current ?? form, file));
  }

  function handleNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setDraft((current) => applyTutorialVideoName(current ?? form, event.target.value));
  }

  function handleModeChange(mode: string): void {
    setDraft((current) =>
      applyTutorialVideoMode(current ?? form, mode as TutorialVideoMode, roles),
    );
  }

  function handleRolesChange(keys: string[]): void {
    setDraft((current) => applyTutorialVideoRoleKeys(current ?? form, roleChoices, keys));
  }

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier la vidéo"
      subtitle="Visible uniquement pour les rôles sélectionnés"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={saveMutation.isPending}
      isValid={isValid}
      isLoading={detailQuery.isLoading || (!detailQuery.data && !detailQuery.isError)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <Clapperboard className="w-4 h-4" />
          Vidéo
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <FileDropzone
            label="Fichier vidéo"
            accept={TUTORIAL_VIDEO_ACCEPTED_FORMATS}
            maxSize={TUTORIAL_VIDEO_MAX_SIZE_BYTES}
            onFileSelect={handleFileSelect}
            hint="Le fichier déjà enregistré est affiché. Choisissez-en un autre pour le remplacer. MP4, WEBM ou MOV — 50 Mo max."
            value={storedPreview.name}
            previewUrl={storedPreview.url}
          />
          <Input
            label="Nom"
            value={form.name}
            onChange={handleNameChange}
            placeholder="Ex: Présentation plateforme tuteur"
            maxLength={200}
            required
          />
          <Select
            label="Mode"
            options={TUTORIAL_MODE_OPTIONS}
            value={form.mode}
            onChange={handleModeChange}
            required
          />
          <MultiSelect
            label="Rôles"
            placeholder="Aucun rôle — visible seulement par l'administration"
            options={roleOptions}
            value={selectedRoles}
            onChange={handleRolesChange}
            helperText="Les rôles suivent le mode. L'administration est toujours incluse."
          />
        </div>
      </div>
    </SlidePanelForm>
  );
};
